import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { QueryReturnValue } from "@reduxjs/toolkit/dist/query/baseQueryTypes";

const DEFAULT_PAGE_SIZE = 500;

interface PaginatedResponse {
  status: string;
  results: number;
  data: any[];
  total: number;
  page: number;
}

type FetchBaseQuery = BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError>;

/**
 * Fetches all pages of a paginated API endpoint by making parallel requests.
 *
 * 1. Fetches the first page to learn the total count.
 * 2. If total > page size, fetches remaining pages in parallel.
 * 3. Merges all `data` arrays into a single response matching the backend shape.
 *
 * Usage inside an RTK Query `queryFn`:
 * ```ts
 * queryFn: async (body, _api, _opts, baseQuery) =>
 *   fetchAllPages(baseQuery, "/keys/?&page=1&limit=500", { authorization: body.token }),
 * ```
 */
export default async function fetchAllPages(
  baseQuery: FetchBaseQuery,
  url: string,
  headers: Record<string, string>,
  pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<QueryReturnValue<PaginatedResponse, FetchBaseQueryError>> {
  const firstPageUrl = replacePageParam(url, 1, pageSize);
  const firstResult = await baseQuery(
    { url: firstPageUrl, headers },
    undefined as any,
    undefined as any,
  );

  if (firstResult.error) {
    return { error: firstResult.error as FetchBaseQueryError };
  }

  const firstPage = firstResult.data as PaginatedResponse;
  const total = firstPage.total ?? 0;
  const fetched = firstPage.data?.length ?? 0;

  if (fetched >= total || total <= pageSize) {
    return { data: firstPage };
  }

  const totalPages = Math.ceil(total / pageSize);
  const remainingPageNumbers = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

  const results = await Promise.all(
    remainingPageNumbers.map((page) =>
      baseQuery(
        { url: replacePageParam(url, page, pageSize), headers },
        undefined as any,
        undefined as any,
      ),
    ),
  );

  const errorResult = results.find((r) => r.error);
  if (errorResult) {
    return { error: errorResult.error as FetchBaseQueryError };
  }

  const allData = [
    ...firstPage.data,
    ...results.flatMap((r) => (r.data as PaginatedResponse).data ?? []),
  ];

  return {
    data: {
      status: "success",
      results: allData.length,
      data: allData,
      total,
      page: 1,
    },
  };
}

function replacePageParam(url: string, page: number, limit: number): string {
  let result = url;

  if (/page=\d+/.test(result)) {
    result = result.replace(/page=\d+/, `page=${page}`);
  } else {
    result += `${result.includes("?") ? "&" : "?"}page=${page}`;
  }

  if (/limit=\d+/.test(result)) {
    result = result.replace(/limit=\d+/, `limit=${limit}`);
  } else {
    result += `&limit=${limit}`;
  }

  return result;
}
